import { Component, Input, ElementRef, ChangeDetectorRef, inject, output, input, viewChild, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { QuillModule } from 'ngx-quill';
import { MaterialModule } from '../../material.module';
import { JkMinioService, MinioImageItem } from '../../services/jk-minio.service';

@Component({
  selector: 'app-jk-rich-editor',
  standalone: true,
  imports: [FormsModule, QuillModule, MaterialModule],
  templateUrl: './jk-rich-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host { display: block; grid-column: 1 / -1; min-width: 0; width: 100%; }
    .jk-rich-editor { min-width: 0; width: 100%; }
    ::ng-deep .jk-rich-editor .ql-container { width: 100%; }
    ::ng-deep .jk-rich-editor quill-editor { display: flex !important; flex-direction: column; }
    ::ng-deep .jk-rich-editor quill-editor .ql-toolbar { flex: 0 0 auto; }
    ::ng-deep .jk-rich-editor quill-editor .ql-container { flex: 1 1 auto; min-height: 0; height: auto; }
    ::ng-deep .jk-rich-editor quill-editor .ql-editor { min-height: 0; }
    ::ng-deep .ql-editor img { max-width: 100%; height: auto; }
  `],
})
export class JkRichEditorComponent {
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() value: string = '';
  readonly valueChange = output<string>();

  readonly readonly = input<boolean>(false);
  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input() label: string = '';
  readonly required = input<boolean>(false);
  readonly path = input<string>('');
  readonly host = input<string>('');
  readonly minHeight = input<string>('250px');

  get editorValue(): string {
    return this.value;
  }

  readonly imageInput = viewChild.required<ElementRef<HTMLInputElement>>('imageInput');

  private quillInstance: any;
  showMinioImages = false;
  showRawText = false;
  minioImages: MinioImageItem[] = [];
  minioImagesLoading = false;

  private minioService = inject(JkMinioService);
  private cdr = inject(ChangeDetectorRef);

  quillModules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ header: [1, 2, 3, false] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ color: [] }, { background: [] }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: () => this.triggerImageUpload(),
      },
    },
  };

  onQuillEditorCreated(quill: any) {
    this.quillInstance = quill;
  }

  triggerImageUpload() {
    this.imageInput()?.nativeElement.click();
  }

  async onImageFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    input.value = '';
    try {
      const url = await this.minioService.uploadImage(this.host(), this.path(), file);
      this.insertImageToEditor(url);
    } catch (e) {
      console.error('上傳圖片至 MinIO 失敗', e);
      alert('上傳圖片失敗，請確認 MinIO 服務已啟動');
    }
  }

  private insertImageToEditor(url: string) {
    if (!this.quillInstance) return;
    const range = this.quillInstance.getSelection(true);
    this.quillInstance.insertEmbed(range.index, 'image', url);
    this.quillInstance.setSelection(range.index + 1);
    this.emitEditorHtml();
  }

  async toggleMinioImages() {
    this.showMinioImages = !this.showMinioImages;
    if (!this.showMinioImages || this.minioImages.length > 0) return;
    this.minioImagesLoading = true;
    this.minioImages = [];
    try {
      this.minioImages = await this.minioService.listImages(this.host(), this.path());
    } catch (e) {
      console.error('載入 MinIO 圖片失敗', e);
    } finally {
      this.minioImagesLoading = false;
      this.cdr.detectChanges();
    }
  }

  toggleRawText(): void {
    this.showRawText = !this.showRawText;
  }

  async pastePlainText(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      this.insertPlainTextAsHtml(text);
    } catch {
      alert('無法讀取剪貼簿，請確認瀏覽器已授權剪貼簿存取。');
    }
  }

  async pasteHtml(): Promise<void> {
    try {
      const html = await navigator.clipboard.readText();
      this.insertHtmlSource(html);
    } catch {
      alert('無法讀取剪貼簿，請確認瀏覽器已授權剪貼簿存取。');
    }
  }

  insertMinioImage(url: string) {
    this.insertImageToEditor(url);
    this.showMinioImages = false;
  }

  /** 將純文字安全地轉為 HTML，並貼入目前游標位置。 */
  insertPlainTextAsHtml(text: string) {
    if (!this.quillInstance || !text) return;

    const html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\r\n|\r|\n/g, '<br>');
    const range = this.quillInstance.getSelection(true);
    console.log('插入純文字為 HTML', { text, html, range });
    this.quillInstance.clipboard.dangerouslyPasteHTML(range.index, html, 'user');
    this.quillInstance.setSelection(range.index + text.length);
    this.emitEditorHtml();
  }

  /** 將剪貼簿中的 HTML 原始碼貼入目前游標位置。 */
  insertHtmlSource(html: string): void {
    if (!this.quillInstance || !html) return;

    const range = this.quillInstance.getSelection(true);
    const previousLength = this.quillInstance.getLength();
    this.quillInstance.clipboard.dangerouslyPasteHTML(range.index, html, 'user');
    const insertedLength = Math.max(0, this.quillInstance.getLength() - previousLength);
    this.quillInstance.setSelection(range.index + insertedLength);
    this.emitEditorHtml();
  }

  onModelChange(val: string): void {
    this.updateValue(this.serializeEditorHtml(val));
  }

  private emitEditorHtml(): void {
    if (!this.quillInstance) return;
    this.updateValue(this.serializeEditorHtml(this.quillInstance.root?.innerHTML || ''));
  }

  /**
   * Quill 將每個文字行正規化為 <p>。對外儲存時還原為 <br>，
   * 讓純文字貼上的換行格式不會因 Quill 的內部 DOM 而改變。
   */

  private serializeEditorHtml(html: string): string {
    return html;
    //  .replace(/<p(?:\s[^>]*)?>([\s\S]*?)<\/p>/g, '$1<br>')
    //  .replace(/<br>$/, '');
  }

  private updateValue(value: string) {
    if (this.value !== value) {
      this.value = value;
      this.valueChange.emit(this.value);
    }
  }
}
